import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import styles from './AuthPage.module.css';
import { notify } from '../utils/toastConfig';

type AuthMode = 'initial' | 'login' | 'register' | 'verify';

interface AuthState {
  email: string;
  name: string;
  pin: string;
}

const AuthPage: React.FC = () => {
  const { loginWithGoogle, register, requestPin, verifyPin } = useAuth();
  const [mode, setMode] = useState<AuthMode>('initial');
  const [authState, setAuthState] = useState<AuthState>({
    email: '',
    name: '',
    pin: '',
  });
  const [formError, setFormError] = useState<string>('');

  const serviceName = process.env.REACT_APP_SERVICE_NAME;

  const renderLogo = () => (
    <a href="/" style={{ textDecoration: 'none' }} className={styles.logoContainer}>
      <div className={styles.logoOrb}>G</div>
      {serviceName || 'Goodence'}
    </a>
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get('error');
    if (error) {
      notify.error(decodeURIComponent(error));
    }
  }, []);

  const handleGoogleLogin = (e: React.MouseEvent) => {
    e.preventDefault();
    loginWithGoogle();
  };

  const handleEmailOption = (e: React.MouseEvent) => {
    e.preventDefault();
    setMode('login');
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    console.log('🔵 1. Starting registration process');

    if (!authState.name.trim()) {
      setFormError('Name is required for registration');
      return;
    }

    if (!authState.email.trim()) {
      setFormError('Email is required for registration');
      return;
    }

    console.log('🟡 3. About to try registration');

    try {
      console.log('🟢 4. Calling register function with:', {
        email: authState.email,
        name: authState.name
      });

      const response = await register(authState.email, authState.name)
        .catch(error => {
          console.log('🔴 4a. Register promise rejected:', error);
          throw error;
        });

      console.log('✅ 5. Register response received:', response);

      if (!response) {
        setFormError('No response from server');
        return;
      }

      if (response.success) {
        console.log('✨ 6b. Registration successful');
        setMode('verify');
      } else {
        if (response.error?.includes('duplicate')) {
          setFormError('This email is already registered. Please try logging in instead.');
          setMode('login');
        } else {
          setFormError(response.error || 'Registration failed. Please try again.');
        }
      }
    } catch (error) {
      setFormError('An unexpected error occurred. Please try again later.');
    } finally {
      console.log('🏁 8. Registration process completed');
    }
  };

  const handleRequestPin = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (!authState.email) {
      setFormError('Please enter your email');
      return;
    }

    try {
      console.log('ooooffffooooffffooooffffooooffffooooffffooooffffooooffffooooffff')
      const response = await requestPin(authState.email);
      if (response.success) {
        setMode('verify');
      } else if (response.error) {
        setFormError(response.error);
      }
    } catch (error) {
      setFormError('Error requesting PIN. Please try again.');
    }
  };

  const handleVerifyPin = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    try {
      const response = await verifyPin(authState.email, authState.pin);
      if (!response.success && response.error) {
        setFormError(response.error);
      }
    } catch (error) {
      setFormError('Error verifying PIN.');
    }
  };

  const renderInitialView = () => (
    <div className={styles.authOptions}>
      {renderLogo()}
      {formError && (
        <div style={{ background: '#fee2e2', color: '#991b1b', padding: '12px', borderRadius: '4px', marginBottom: '16px', fontSize: '14px', border: '1px solid #fecaca', textAlign: 'center' }}>
          {formError}
        </div>
      )}
      <div className={styles.buttonContainer}>
        <button className={styles.googleButton} onClick={handleGoogleLogin}>
          Continue with Google
        </button>
        <button className={styles.emailButton} onClick={handleEmailOption}>
          Continue with Email
        </button>
      </div>
    </div>
  );

  const renderLoginView = () => (
    <div className={styles.formContainer}>
      {renderLogo()}
      <div className={styles.authSubtitle}>Login with your Email</div>
      {formError && (
        <div style={{ background: '#fee2e2', color: '#991b1b', padding: '12px', borderRadius: '4px', marginBottom: '16px', fontSize: '14px', border: '1px solid #fecaca', textAlign: 'center' }}>
          {formError}
        </div>
      )}
      <form onSubmit={handleRequestPin}>
        <input
          type="email"
          value={authState.email}
          onChange={e => setAuthState(prev => ({ ...prev, email: e.target.value }))}
          placeholder="Enter your email"
          required
        />
        <div className={styles.buttonGroup}>
          <button type="submit" className={styles.primaryButton}>Send PIN</button>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={() => setMode('register')}
          >
            New user? Register
          </button>
        </div>
      </form>
    </div>
  );

  const renderRegisterView = () => (
    <div className={styles.formContainer}>
      {renderLogo()}
      <div className={styles.authSubtitle}>Create your account</div>
      {formError && (
        <div style={{ background: '#fee2e2', color: '#991b1b', padding: '12px', borderRadius: '4px', marginBottom: '16px', fontSize: '14px', border: '1px solid #fecaca', textAlign: 'center' }}>
          {formError}
        </div>
      )}
      <form onSubmit={handleRegister}>
        <input
          type="text"
          value={authState.name}
          onChange={e => setAuthState(prev => ({ ...prev, name: e.target.value }))}
          placeholder="Your name"
          required
        />
        <input
          type="email"
          value={authState.email}
          onChange={e => setAuthState(prev => ({ ...prev, email: e.target.value }))}
          placeholder="Your email"
          required
        />
        <div className={styles.buttonGroup}>
          <button type="submit" className={styles.primaryButton}>Register</button>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={() => setMode('login')}
          >
            Already registered? Login
          </button>
        </div>
      </form>
    </div>
  );

  const renderVerifyView = () => (
    <div className={styles.formContainer}>
      {renderLogo()}
      <div className={styles.authSubtitle}>Enter your Verification PIN</div>
      <div style={{ background: '#dcfce7', color: '#166534', padding: '12px', borderRadius: '4px', marginBottom: '16px', fontSize: '14px', border: '1px solid #bbf7d0', textAlign: 'center' }}>
        ✅ PIN sent to <strong>{authState.email}</strong>
      </div>
      {formError && (
        <div style={{ background: '#fee2e2', color: '#991b1b', padding: '12px', borderRadius: '4px', marginBottom: '16px', fontSize: '14px', border: '1px solid #fecaca', textAlign: 'center' }}>
          {formError}
        </div>
      )}
      <form onSubmit={handleVerifyPin}>
        <input
          type="text"
          value={authState.pin}
          onChange={e => setAuthState(prev => ({ ...prev, pin: e.target.value.replace(/\D/g, '') }))}
          placeholder="Enter 6-digit PIN"
          maxLength={6}
          pattern="\d{6}"
          required
        />
        <button type="submit" className={styles.primaryButton}>Verify PIN</button>
      </form>
    </div>
  );

  const renderView = () => {
    switch (mode) {
      case 'initial':
        return renderInitialView();
      case 'login':
        return renderLoginView();
      case 'register':
        return renderRegisterView();
      case 'verify':
        return renderVerifyView();
      default:
        return renderInitialView();
    }
  };

  return (
    <div className={styles.authPage}>
      {renderView()}
    </div>
  );
};

export default AuthPage; 